import saveAs from "file-saver";
import qs from "qs";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useRef } from "react";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export enum BillStatus {
  PENDING,
  GENERATED,
  SENT,
  ERROR,
  CONFIRMED,
}

export interface CustomerBillType {
  id: number;
  type: string;
  status: BillStatus;
  dueDate: Date;
  startDate: Date;
  endDate: Date;
  amount: number;
  isDeleted: boolean;
  excludeZero: boolean;
  ref: string;
  clientInvoiceRate: ClientInvoiceRate[];
  profileId: string | string[];
  createdDate: string;
  sentBy: string;
  createdBy: string;
  confirmedBy: string;
}
export interface ClientInvoiceRate {
  id: number;
  count: number;
  totalRate: number;
  isDeleted: boolean;
  clientConnectionProfileId: number[];
  clientInvoiceId: string;
}

export interface CustomerBillHistory {
  id: string;
  snapshot: CustomerBillType & { timestamp: string };
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
  sentBy: string;
  confirmedBy: string;
}

export interface CustomerBillHistoryDiff {
  sentBy: string;
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
  snapshot: CustomerBillType & { timestamp: string };
  diff?: Record<string, string>;
}

export async function fetchCustomerBill(
  params: Params<CustomerBillType>
): Promise<IPageable<CustomerBillType>> {
  try {
    const response = await axiosInstance.get("/invoices/clients", { params });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the users."
    );
  }
}

export async function fetchConfirmedCustomerBills(params: {
  page: number;
  size: number;
}): Promise<{
  content: { id: string; ref: string }[];
  totalElements: number;
  totalPages: number;
}> {
  try {
    const response = await axiosInstance.get("/invoices/clients", {
      params: { ...params, status: "CONFIRMED", excludeZero: true },
    });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching customer bills:", error);
    throw error;
  }
}

export interface BulkGenerateRequest {
  connectionProfileIds: number[];
  periods: { month: number; year: number }[];
}

export interface BulkGenerateResult {
  month: number;
  year: number;
  invoiceId: number;
  invoiceRef: string;
  success: boolean;
  errorMessage?: string;
}

export interface BulkGenerateResponse {
  totalRequested: number;
  successCount: number;
  failureCount: number;
  results: BulkGenerateResult[];
}

export const createCustomerBill = async (
  params: BulkGenerateRequest
): Promise<BulkGenerateResponse> => {
  try {
    const response = await axiosInstance.post(`/invoices/clients/bulk-generate`, params);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.errorMessage ||
        "An error occurred while generating customer bills."
    );
  }
};

export const fetchAccountsByProfile = async (profileId: string) => {
  try {
    const response = await axiosInstance.get(
      `/connection-profiles/clients?profileId=${profileId}`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching accounts for profile:", error);
    throw error;
  }
};

export const pdfApi = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/invoices/clients/${id}/pdf`, {
      responseType: "blob",
    });

    // Extract filename from Content-Disposition header
    const disposition = response.headers["content-disposition"];
    let filename = `invoice-${id}.pdf`;
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "application/pdf" });

    saveAs(blob, filename);

    return response.data;
  } catch (error: any) {
    console.error(
      "Error fetching the PDF:",
      error.response?.status,
      error.response?.data
    );
  }
};

export const sendEmails = async (
  ids: string[],
  attachCcEmail?: boolean
): Promise<any> => {
  try {
    const body: { ids: number[]; attachCcEmail?: boolean } = {
      ids: ids.map(Number),
    };
    if (attachCcEmail !== undefined) {
      body.attachCcEmail = attachCcEmail;
    }

    const response = await axiosInstance.post("/invoices/clients/send", body);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || "An error occurred while sending emails."
    );
  }
};

export const sendSingleEmail = async (
  id: string,
  attachCcEmail?: boolean
): Promise<any> => {
  try {
    const params: { attachCcEmail?: boolean } = {};
    if (attachCcEmail !== undefined) {
      params.attachCcEmail = attachCcEmail;
    }
    const queryString = qs.stringify(params, { arrayFormat: "comma" });

    const response = await axiosInstance.post(
      `/invoices/clients/${id}/send?${queryString}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while sending email for ID: ${id}`
    );
  }
};

export async function fetchCustomerBillById(
  id: CustomerBillType["id"]
): Promise<CustomerBillType> {
  try {
    const response = await axiosInstance.get(`/invoices/clients/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting invoices clients/${id}.`
    );
  }
}

export async function fetchCustomerBillHistory(
  id: CustomerBillType["id"],
  params: Params<CustomerBillType> = {}
): Promise<IPageable<CustomerBillType>> {
  try {
    const response = await axiosInstance.get(`/billings/history`, {
      params: { mainId: id, sort: "historyTimestamp,desc", ...params },
    });
    const data = response.data;
    return {
      ...data,
      content: data.content.map((item: any) => ({
        ...item.snapshot,
        historyTimestamp: item.historyTimestamp,
      })),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting the Customer Bill history for ID ${id}.`
    );
  }
}

export const useCustomerBillQuery = (
  params: Params<CustomerBillType>
): UseQueryResult<IPageable<CustomerBillType>, Error> => {
  const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["customerBill", JSON.stringify(effectiveParams)];

  const queryResult = useQuery({
    queryKey,
    queryFn: () => fetchCustomerBill(effectiveParams),
    // Poll every 2 seconds while any invoice has PENDING status
    refetchInterval: (query) => {
      const data = (query as any)?.state?.data as IPageable<CustomerBillType> | undefined;
      const hasPending = data?.content?.some(
        (item) => item.status === BillStatus.PENDING || item.status === ("PENDING" as any)
      );
      return hasPending ? 2000 : false;
    },
  });

  return queryResult;
};

export const useCustomerBillById = (
  id: CustomerBillType["id"]
): UseQueryResult<CustomerBillType, Error> => {
  return useQuery<CustomerBillType, Error>({
    queryKey: ["customerBillById", id],
    queryFn: () => fetchCustomerBillById(id),
  });
};

export const exportCustomerBill = async (
  params: Params<CustomerBillType> = {}
): Promise<void> => {
  try {
    const response = await axiosInstance.get("/invoices/clients/csv", {
      params,
      responseType: "blob",
    });

    const disposition = response.headers["content-disposition"];
    let filename = "Customer Bill.csv";
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, filename);

    console.log("Export successful and file downloaded.");
  } catch (error: any) {
    console.error(
      "Error fetching customer:",
      error.response?.data?.message || error.message || error
    );
  }
};

export const pdfCustomerDetailsApi = async (id: string) => {
  try {
    const response = await axiosInstance.get(
      `/invoices/clients/${id}/pdf/details`,
      {
        responseType: "blob",
      }
    );

    const disposition = response.headers["content-disposition"];
    let filename = `invoice-${id}.pdf`;
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "application/pdf" });

    saveAs(blob, filename);

    return response.data;
  } catch (error: any) {
    console.error(
      "Error fetching the PDF:",
      error.response?.status,
      error.response?.data
    );
  }
};

export const confirmCustomerBill = async (
  id: string | number
): Promise<CustomerBillType> => {
  try {
    const response = await axiosInstance.post(
      `/invoices/clients/${id}/confirm`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while confirming the customer bill."
    );
  }
};

export const deleteCustomerBills = async (
  ids: string[]
): Promise<void> => {
  try {
    const response = await axiosInstance.delete(`/invoices/clients`, { data: ids });
    // Check if the response contains an error message (some APIs return 200 with error in body)
    if (response.data?.error || response.data?.message) {
      throw new Error(response.data.message || response.data.error);
    }
    console.log(`Customer Bills with IDs ${ids} deleted successfully.`);
  } catch (error: any) {
    // Extract error message from axios error response or use the error message directly
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      "An error occurred while deleting the customer bills.";
    throw new Error(errorMessage);
  }
};
